/**
 * Default home page CMS content — keep in sync with backend/constants/homePageDefaults.js
 */

import {
  normalizeHeroCarouselOrder,
  DEFAULT_HERO_CAROUSEL_ORDER,
  type HeroCarouselSlideItem,
} from '../utils/heroCarouselOrder';

export type {
  HeroCarouselSlideType,
  HeroCarouselCustomSlide,
  HeroCarouselSlideItem,
} from '../utils/heroCarouselOrder';

export interface TickerItem {
  text: string;
}

export interface StatItem {
  value: string;
  label: string;
}

export interface ValuePropCard {
  title: string;
  description: string;
}

export interface TestimonialItem {
  quote: string;
  author: string;
}

export interface HomePageContent {
  newsTicker: TickerItem[];
  heroSlide: {
    titleLine1: string;
    titleLine2: string;
    subtitle: string;
  };
  successStoriesSlide: {
    title: string;
    count: string;
    headline: string;
    salaryPrefix: string;
    salaryRangeText: string;
    salarySuffix: string;
    description: string;
    stats: StatItem[];
    primaryCta: string;
    secondaryCta: string;
  };
  mentorsSlide: {
    title: string;
    subtitle: string;
    emptyTitle: string;
    emptySubtitle: string;
    primaryCta: string;
    secondaryCta: string;
  };
  trustIndicators: StatItem[];
  valueProposition: {
    title: string;
    subtitle: string;
    cards: ValuePropCard[];
  };
  testimonials: {
    title: string;
    items: TestimonialItem[];
  };
  sponsorsSection: {
    title: string;
    subtitle: string;
    footerNote: string;
  };
  finalCta: {
    title: string;
    subtitle: string;
    primaryCta: string;
    secondaryCta: string;
  };
  carouselLabels: {
    viewAllHackathons: string;
    viewAllEvents: string;
    learnMore: string;
  };
  heroCarouselOrder: HeroCarouselSlideItem[];
}

export const HOME_PAGE_DEFAULTS: HomePageContent = {
  newsTicker: [
    { text: '🚀 Hackathon on Aug 14-16 - Please keep watching this space soon!' },
    { text: '🎉 25 Aspirants placed with support and guidance from Udyoga Pramoda' },
    { text: '📍 Started Kalburgi Chapter of Udyoga Pramoda' },
  ],
  heroSlide: {
    titleLine1: 'Empowering',
    titleLine2: 'Careers',
    subtitle: 'Connect with experienced mentors, attend transformative events, and build the future you envision.',
  },
  successStoriesSlide: {
    title: 'Success Stories',
    count: '25',
    headline: 'Aspirants Successfully Placed',
    salaryPrefix: 'With salary packages ranging from',
    salaryRangeText: '₹3 Lakhs to ₹12 Lakhs',
    salarySuffix: 'per annum',
    description: 'Join our thriving community and unlock your career potential.',
    stats: [
      { value: '100%', label: 'Job Placement' },
      { value: '₹7.5L', label: 'Avg. Salary' },
      { value: '156+', label: 'Mentors' },
    ],
    primaryCta: 'Join Success Story',
    secondaryCta: 'Browse Opportunities',
  },
  mentorsSlide: {
    title: 'Expert Mentors',
    subtitle: 'Learn from industry leaders at top companies and accelerate your career growth.',
    emptyTitle: 'No mentors available at the moment',
    emptySubtitle: 'Check back soon for our featured mentors!',
    primaryCta: 'Find Your Mentor',
    secondaryCta: 'Become a Mentor',
  },
  trustIndicators: [
    { value: '1,247+', label: 'Active Users' },
    { value: '89+', label: 'Events' },
    { value: '156+', label: 'Mentors' },
  ],
  valueProposition: {
    title: 'Why Choose Udyoga Pramoda?',
    subtitle: "We've designed a platform that puts your career growth first, with proven results and meaningful connections.",
    cards: [
      {
        title: 'Connect with Mentors',
        description: "Build meaningful relationships with experienced professionals who have walked the path you're on.",
      },
      {
        title: 'Attend Events',
        description: 'Participate in curated workshops, seminars, and networking events designed to accelerate your growth.',
      },
      {
        title: 'Grow Your Career',
        description: 'Access exclusive resources, opportunities, and insights to advance your professional journey.',
      },
    ],
  },
  testimonials: {
    title: '"Testimonials"',
    items: [
      {
        quote: 'I am immensely grateful to the Udyog Pramoda Initiative for the invaluable support and mentorship that helped me develop my skills. The guidance from mentors played a crucial role in securing a job offer at Deloitte as an Audit Analyst. This marks the beginning of my professional journey, and I look forward to the opportunities and growth ahead.',
        author: 'Harshini Shroff',
      },
      {
        quote: 'Udyog Pramoda transformed my job search approach. I learned the importance of ATS-friendly resumes, consistent LinkedIn engagement, and upskilling. These sessions not only boosted my confidence but also reinforced my belief that with the right strategy and continuous learning, I can achieve my dream job.',
        author: 'Akshata Deshpande',
      },
      {
        quote: 'Udyog Pramoda has been a game-changer in my career journey. Their guidance and support helped me achieve to shape my professional career and making the process seamless. I highly recommend them to anyone looking for career growth!',
        author: 'Vishnu Tej Patlolla.',
      },
      {
        quote: 'Udyog Pramoda has been instrumental in boosting my confidence and interview readiness. I gained invaluable strategies on showcasing experience, tackling interview questions, and optimizing my resume for ATS. A special thanks to my mentor, Prashanth Nandagiri Anna, whose guidance helped me refine my resume and secure a Product Owner role at RingCentral. This forum goes beyond job opportunities—it empowers individuals with the confidence to succeed.',
        author: 'Vanitha Joshi',
      },
    ],
  },
  sponsorsSection: {
    title: 'Powered By Industry Leaders',
    subtitle: 'Built with cutting-edge technology and supported by trusted partners',
    footerNote: 'Delivering enterprise-grade reliability and performance',
  },
  finalCta: {
    title: 'Ready to Transform Your Career?',
    subtitle: 'Join thousands of professionals who have already taken the first step towards their dream careers.',
    primaryCta: 'Start Your Journey',
    secondaryCta: 'Explore Events',
  },
  carouselLabels: {
    viewAllHackathons: 'View All Hackathons',
    viewAllEvents: 'View All Events',
    learnMore: 'Learn More',
  },
  heroCarouselOrder: DEFAULT_HERO_CAROUSEL_ORDER,
};

function deepMergeSection<T>(defaults: T, stored: Partial<T> | null | undefined): T {
  if (!stored || typeof stored !== 'object') {
    return JSON.parse(JSON.stringify(defaults)) as T;
  }
  const result = JSON.parse(JSON.stringify(defaults)) as T;
  for (const key of Object.keys(defaults as object) as Array<keyof T>) {
    const defaultVal = defaults[key];
    const storedVal = stored[key];
    if (Array.isArray(defaultVal)) {
      result[key] = (Array.isArray(storedVal) && (storedVal as unknown[]).length > 0
        ? storedVal
        : defaultVal) as T[keyof T];
    } else if (defaultVal && typeof defaultVal === 'object') {
      result[key] = deepMergeSection(
        defaultVal,
        storedVal as Partial<typeof defaultVal> | undefined
      ) as T[keyof T];
    } else {
      result[key] = (storedVal !== undefined && storedVal !== null ? storedVal : defaultVal) as T[keyof T];
    }
  }
  return result;
}

export function mergeHomePageContent(
  stored?: Partial<HomePageContent> | null,
  legacy?: { heroTitle?: string; heroSubtitle?: string }
): HomePageContent {
  const merged = deepMergeSection(HOME_PAGE_DEFAULTS, stored);
  if (legacy?.heroTitle?.trim() && !stored) {
    merged.heroSlide.titleLine1 = legacy.heroTitle.trim();
    if (legacy.heroSubtitle?.trim()) {
      merged.heroSlide.subtitle = legacy.heroSubtitle.trim();
    }
  }
  merged.heroCarouselOrder = normalizeHeroCarouselOrder(merged.heroCarouselOrder);
  return merged;
}
